import { useEffect, useState } from "react";
import toast from "../../../utils/toast";
import {
  getBillTypeList,
  createBillTypes,
  getBillTypesById,
  updateBillTypesById,
  toggleBillTypeStatusById,
} from "../../../services/propertyOwner/propertyOwnerServices";

export default function OwnerBillTypeMaster() {
  const [billTypeList, setBillTypeList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [isModalOpenAdd, setIsModalOpenAdd] = useState(false);
  const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [addErrors, setAddErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});

  const INITIAL_FORM = {
    name: "",
    icon: "",
    description: "",
    frequency: "Monthly",
    chargeType: "fixed",
    isPartialAllowed: false,
    isActive: true,
    sortOrder: 0,
  };

  const [addForm, setAddForm] = useState(INITIAL_FORM);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchBillTypeList();
  }, []);

  const fetchBillTypeList = async () => {
    setListLoading(true);
    try {
      const res = await getBillTypeList();
      if (res.statusCode === 1) setBillTypeList(res.data);
    } catch (err) {
      console.log("Failed to fetch bill types");
    } finally {
      setListLoading(false);
    }
  };

  // Add
  const openAddModal = () => {
    setAddForm(INITIAL_FORM);
    setAddErrors({});
    setIsModalOpenAdd(true);
  };

  const closeAddModal = () => {
    setIsModalOpenAdd(false);
    setAddForm(INITIAL_FORM);
    setAddErrors({});
  };

  const handleAddChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setAddErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCreateBillType = async () => {
    const e = {};
    if (!addForm.name) e.name = "Charge name is required.";
    if (Object.keys(e).length) {
      setAddErrors(e);
      return;
    }

    setAddLoading(true);
    try {
      const res = await createBillTypes({
        name: addForm.name,
        icon: addForm.icon,
        description: addForm.description,
        frequency: addForm.frequency,
        chargeType: addForm.chargeType,
        isPartialAllowed: addForm.isPartialAllowed,
        isActive: addForm.isActive,
        sortOrder: Number(addForm.sortOrder),
      });
      if (res.statusCode === 1) {
        toast.success(res.message);

        closeAddModal();
        fetchBillTypeList();
      } else {
        toast.error(res.message);
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAddLoading(false);
    }
  };

  // Edit
  const openEditModal = async (id) => {
    try {
      const res = await getBillTypesById(id);
      if (res.statusCode === 1) {
        const d = res.data;
        setEditForm({
          name: d.name || "",
          icon: d.icon || "",
          description: d.description || "",
          frequency: d.frequency || "Monthly",
          chargeType: d.chargeType || "fixed",
          isPartialAllowed: d.isPartialAllowed,
          isActive: d.isActive,
          sortOrder: d.sortOrder || 1,
        });
        setSelectedBillId(id);
        setEditErrors({});
        setIsModalOpenEdit(true);
      }
    } catch (err) {
      toast.error("Failed to load bill type.");
    }
  };

  const closeEditModal = () => {
    setIsModalOpenEdit(false);
    setEditForm({});
    setEditErrors({});
    setSelectedBillId(null);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleUpdateBillType = async () => {
    const e = {};
    if (!editForm.name) e.name = "Charge name is required.";
    if (Object.keys(e).length) {
      setEditErrors(e);
      return;
    }

    setEditLoading(true);
    try {
      const res = await updateBillTypesById(selectedBillId, {
        name: editForm.name,
        icon: editForm.icon,
        description: editForm.description,
        frequency: editForm.frequency,
        chargeType: editForm.chargeType,
        isPartialAllowed: editForm.isPartialAllowed,
        isActive: editForm.isActive,
        sortOrder: Number(editForm.sortOrder),
      });
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeEditModal();
        fetchBillTypeList();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };

  // Toggle
  const handleToggle = async (bill) => {
    const newValue = !bill.isActive;
    setBillTypeList((prev) =>
      prev.map((b) =>
        b.billTypeId === bill.billTypeId ? { ...b, isActive: newValue } : b,
      ),
    );
    try {
      const res = await toggleBillTypeStatusById(bill.billTypeId, newValue);
      if (res.statusCode === 1) {
        toast.success(res.message);
        toast.success(res.message);
      } else {
        toast.error(res.message);
        setBillTypeList((prev) =>
          prev.map((b) => (b.billTypeId === bill.billTypeId ? bill : b)),
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
      setBillTypeList((prev) =>
        prev.map((b) => (b.billTypeId === bill.billTypeId ? bill : b)),
      );
    }
  };
  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Bill Type Master
            </div>
            <div className="page-sub" id="page-sub">
              Add, edit, reorder and activate bill charge types
            </div>
          </div>
          <div className="topbar-right" id="topbar-right">
            <button className="btn btn-primary" onClick={openAddModal}>
              + Add Bill Type
            </button>
          </div>
        </div>
        <div className="content" id="content-area">
          <div className="info-box mb16">
            <strong>9 of 9 bill types active.</strong> You can add, edit,
            reorder, and activate/deactivate bill types here. Active bill types
            are auto-populated when adding or editing a room.
          </div>
          <div className="card">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 14,
              }}
            >
              <div className="card-title" style={{ margin: 0 }}>
                Bill Types
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>Order</th>
                    <th style={{ width: 40 }}>Icon</th>
                    <th style={{ minWidth: 130 }}>Charge Name</th>
                    <th>Description</th>
                    <th style={{ minWidth: 110 }}>Frequency</th>
                    <th style={{ minWidth: 130 }}>Charge Type</th>
                    <th style={{ minWidth: 120 }}>Partial Pay</th>
                    <th style={{ width: 70 }}>Active</th>
                    <th style={{ minWidth: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listLoading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 9 }).map((_, j) => (
                          <td key={j}>
                            <div
                              style={{
                                height: 14,
                                borderRadius: 6,
                                background:
                                  "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite",
                                width: "70%",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : billTypeList.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          textAlign: "center",
                          color: "var(--text-tertiary)",
                          padding: 24,
                        }}
                      >
                        No bill types found.
                      </td>
                    </tr>
                  ) : (
                    billTypeList.map((bill, index) => (
                      <tr key={bill.billTypeId}>
                        <td className="sn-col">{bill.sortOrder}</td>
                        <td>
                          <span style={{ fontSize: 18 }}>{bill.icon}</span>
                        </td>
                        <td style={{ fontWeight: 600 }}>{bill.name}</td>
                        <td
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                          }}
                        >
                          {bill.description}
                        </td>
                        <td>
                          {bill.chargeType === "PerUnit" ? (
                            <span className="badge badge-teal">Per Unit</span>
                          ) : bill.chargeType === "Percentage" ? (
                            <span className="badge badge-purple">
                              % of Rent
                            </span>
                          ) : (
                            <span className="badge badge-info">₹ Fixed</span>
                          )}
                        </td>
                        <td>
                          <span className="badge badge-info">
                            {bill.frequency}
                          </span>
                        </td>
                        <td>
                          {bill.isPartialAllowed ? (
                            <span className="badge badge-active">✓ Yes</span>
                          ) : (
                            <span className="badge badge-gray">✕ No</span>
                          )}
                        </td>
                        <td>
                          <label
                            className="toggle"
                            style={{ display: "inline-block" }}
                          >
                            <input
                              type="checkbox"
                              checked={bill.isActive}
                              onChange={() => handleToggle(bill)}
                            />
                            <span className="toggle-slider" />
                          </label>
                        </td>
                        <td>
                          <div className="td-actions">
                            <span
                              className="action-icon"
                              title="Edit"
                              onClick={() => openEditModal(bill.billTypeId)}
                            >
                              <span className="tip">Edit</span>✎
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {isModalOpenAdd && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 560 }}>
              <div className="modal-header">
                <div className="modal-title">Add Bill Type</div>
                <button className="modal-close" onClick={closeAddModal}>
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group mb12">
                    <label>Charge Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={addForm.name}
                      onChange={handleAddChange}
                      placeholder="e.g. Rent, Water, Gas, Parking…"
                      style={addErrors.name ? { borderColor: "#dc2626" } : {}}
                    />
                    {addErrors.name && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {addErrors.name}
                      </span>
                    )}
                  </div>
                  <div className="form-group mb12">
                    <label>Icon / Emoji</label>
                    <input
                      type="text"
                      name="icon"
                      value={addForm.icon}
                      onChange={handleAddChange}
                      placeholder="e.g. 🏠 💧 🔥"
                    />
                    <div className="input-hint">Paste any emoji as icon</div>
                  </div>
                  <div className="form-group mb12 full">
                    <label>Description</label>
                    <input
                      type="text"
                      name="description"
                      value={addForm.description}
                      onChange={handleAddChange}
                      placeholder="Brief description"
                    />
                  </div>
                  <div className="form-group mb12">
                    <label>Default Frequency *</label>
                    <select
                      name="frequency"
                      value={addForm.frequency}
                      onChange={handleAddChange}
                    >
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="HalfYearly">Half-Yearly</option>
                      <option value="Yearly">Yearly</option>
                      <option value="OneTime">One-time</option>
                    </select>
                  </div>
                  <div className="form-group mb12">
                    <label>Charge Type *</label>
                    <select
                      name="chargeType"
                      value={addForm.chargeType}
                      onChange={handleAddChange}
                    >
                      <option value="Fixed">₹ Fixed Amount</option>
                      <option value="Percentage">% of Monthly Rent</option>
                      <option value="PerUnit">Per Unit</option>
                    </select>
                  </div>
                  <div className="form-group mb12">
                    <label>Sort Order</label>
                    <input
                      type="number"
                      name="sortOrder"
                      value={addForm.sortOrder}
                      onChange={handleAddChange}
                      min={1}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 24,
                    padding: "12px 14px",
                    background: "var(--bg-secondary)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isPartialAllowed"
                        checked={addForm.isPartialAllowed}
                        onChange={handleAddChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        Allow Partial Payment
                      </div>
                      <div
                        style={{ fontSize: 11, color: "var(--text-secondary)" }}
                      >
                        Tenant can pay partial amount
                      </div>
                    </div>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={addForm.isActive}
                        onChange={handleAddChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        Active
                      </div>
                      <div
                        style={{ fontSize: 11, color: "var(--text-secondary)" }}
                      >
                        Show in room bill charges
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn"
                  onClick={closeAddModal}
                  disabled={addLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateBillType}
                  disabled={addLoading}
                >
                  {addLoading ? "Saving..." : "Save Bill Type"}
                </button>
              </div>
            </div>
          </div>
        )}
        {isModalOpenEdit && (
          <div className="modal-overlay open">
            <div className="modal" style={{ width: 560 }}>
              <div className="modal-header">
                <div className="modal-title">Edit Bill Type</div>
                <button className="modal-close" onClick={closeEditModal}>
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group mb12">
                    <label>Charge Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={editForm.name}
                      onChange={handleEditChange}
                      placeholder="e.g. Rent, Water, Gas, Parking…"
                      style={editErrors.name ? { borderColor: "#dc2626" } : {}}
                    />
                    {editErrors.name && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {editErrors.name}
                      </span>
                    )}
                  </div>
                  <div className="form-group mb12">
                    <label>Icon / Emoji</label>
                    <input
                      type="text"
                      name="icon"
                      value={editForm.icon}
                      onChange={handleEditChange}
                      placeholder="e.g. 🏠 💧 🔥"
                    />
                    <div className="input-hint">Paste any emoji as icon</div>
                  </div>
                  <div className="form-group mb12 full">
                    <label>Description</label>
                    <input
                      type="text"
                      name="description"
                      value={editForm.description}
                      onChange={handleEditChange}
                      placeholder="Brief description"
                    />
                  </div>
                  <div className="form-group mb12">
                    <label>Default Frequency *</label>
                    <select
                      name="frequency"
                      value={editForm.frequency}
                      onChange={handleEditChange}
                    >
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="HalfYearly">Half-Yearly</option>
                      <option value="Yearly">Yearly</option>
                      <option value="OneTime">One-time</option>
                    </select>
                  </div>
                  <div className="form-group mb12">
                    <label>Charge Type *</label>
                    <select
                      name="chargeType"
                      value={editForm.chargeType}
                      onChange={handleEditChange}
                    >
                      <option value="Fixed">₹ Fixed Amount</option>
                      <option value="Percentage">% of Monthly Rent</option>
                      <option value="PerUnit">Per Unit</option>
                    </select>
                  </div>
                  <div className="form-group mb12">
                    <label>Sort Order</label>
                    <input
                      type="number"
                      name="sortOrder"
                      value={editForm.sortOrder}
                      onChange={handleEditChange}
                      min={1}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 24,
                    padding: "12px 14px",
                    background: "var(--bg-secondary)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isPartialAllowed"
                        checked={editForm.isPartialAllowed}
                        onChange={handleEditChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        Allow Partial Payment
                      </div>
                      <div
                        style={{ fontSize: 11, color: "var(--text-secondary)" }}
                      >
                        Tenant can pay partial amount
                      </div>
                    </div>
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      cursor: "pointer",
                    }}
                  >
                    <label className="toggle">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={editForm.isActive}
                        onChange={handleEditChange}
                      />
                      <span className="toggle-slider" />
                    </label>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        Active
                      </div>
                      <div
                        style={{ fontSize: 11, color: "var(--text-secondary)" }}
                      >
                        Show in room bill charges
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn"
                  onClick={closeEditModal}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUpdateBillType}
                  disabled={editLoading}
                >
                  {editLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
